import ArchiveIcon from "@mui/icons-material/Inventory2Outlined";
import { Box, Paper } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridCellParams,
  GridColumns,
  MuiEvent,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  currencyFormatter,
  defaultDataGridProps,
  handleArrowNavigationForNumberCells,
} from "../../../constants";
import { toScaledInteger } from "../../../helpers/numberHelpers";
import {
  setShowArchivedRates,
  showArchivedRates,
} from "../../../redux/filmsReducer";
import { useProjectTypes } from "../../../redux/projectType/projectTypeHooks";
import { useUpdateProjectTypeMutation } from "../../../redux/projectType/projectTypeEndpoints";
import { useScales } from "../../../redux/scale/scaleHooks";
import {
  useCreateScaleMutation,
  useUpdateScaleMutation,
} from "../../../redux/scale/scaleEndpoints";
import { useTaskTypes } from "../../../redux/taskType/taskTypeHooks";
import { Button, Checkbox, Chip } from "@mui/joy";
import { useSaveError } from "../../saveError/saveError";
import ArchiveColumnsDialog from "./archiveColumnsDialog";

export default function JobCategoryRates({
  jobCategoryID,
}: {
  jobCategoryID: number;
}) {
  const showArchived = useSelector(showArchivedRates);
  const { projectTypes: _projectTypes } = useProjectTypes();
  const apiRef = useGridApiRef();
  const { taskTypes: _taskTypes } = useTaskTypes();
  // Archiving is what keeps this grid readable: retired task types drop out of
  // the rows and retired project types out of the columns, until asked for.
  const taskTypes = _taskTypes?.filter(
    (tt) => tt.jobCategoryID === jobCategoryID && (showArchived || !tt.archived)
  );
  const projectTypes = _projectTypes?.filter(
    (pt) => showArchived || !pt.archived
  );
  const [updateProjectType] = useUpdateProjectTypeMutation();
  const [updateScale] = useUpdateScaleMutation();
  const { reportSaveError, saveErrorSnackbar } = useSaveError();
  const { scales } = useScales();
  const [createScale] = useCreateScaleMutation();

  const copyCellValue = async (params: GridCellParams) => {
    const valueToCopy = params.formattedValue ?? params.value;
    if (valueToCopy === undefined || valueToCopy === null) return;

    const text = `${valueToCopy}`;
    if (!text.length) return;

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch (_error) {
        // Fallback below for browsers/contexts that reject clipboard API.
      }
    }

    const tempInput = document.createElement("textarea");
    tempInput.value = text;
    document.body.appendChild(tempInput);
    tempInput.select();
    document.execCommand("copy");
    document.body.removeChild(tempInput);
  };

  const parsePastedValue = (rawValue: string, params: GridCellParams) => {
    if (params.colDef.type !== "number") return rawValue;

    const normalized = rawValue
      .replace(/,/g, "")
      .replace(/\$/g, "")
      .replace(/%/g, "")
      .replace(/\/h/g, "")
      .trim();
    const parsed = Number(normalized);

    return Number.isFinite(parsed) ? parsed : rawValue;
  };

  const handleCellKeyDown = async (
    params: GridCellParams,
    event: MuiEvent<React.KeyboardEvent>
  ) => {
    handleArrowNavigationForNumberCells(params, event);

    const isShortcut = event.metaKey || event.ctrlKey;
    if (!isShortcut || event.altKey) return;

    const key = event.key.toLowerCase();

    if (key === "c") {
      event.preventDefault();
      event.stopPropagation();
      await copyCellValue(params);
      return;
    }

    if (key !== "v" || !params.isEditable) {
      return;
    }
    if (!navigator.clipboard?.readText) return;

    let pastedRawValue = "";
    try {
      pastedRawValue = await navigator.clipboard.readText();
    } catch (_error) {
      // Fall back to native paste if clipboard API is blocked.
      return;
    }
    if (!pastedRawValue) return;

    event.preventDefault();
    event.stopPropagation();

    apiRef.current.startCellEditMode({ id: params.id, field: params.field });
    await apiRef.current.setEditCellValue({
      id: params.id,
      field: params.field,
      value: parsePastedValue(pastedRawValue, params),
    });
    apiRef.current.stopCellEditMode({
      id: params.id,
      field: params.field,
    });
  };

  const processRowUpdate = async (_newRow: any, _oldRow: any) => {
    const saves: Promise<unknown>[] = [];

    projectTypes?.forEach((pt) => {
      if (_newRow[pt.name].rate != _oldRow[pt.name].rate) {
        if (_newRow[pt.name].id === 0) {
          // Min pay for Copyists
          saves.push(
            updateProjectType({
              id: _newRow[pt.name].projectTypeID,
              body: {
                minimumCopyist: _newRow[pt.name].rate,
              },
            }).unwrap()
          );
        } else {
          saves.push(
            updateScale({
              id: _newRow[pt.name].id,
              body: _newRow[pt.name],
            }).unwrap()
          );
        }
      }
    });

    // Rates are edited one at a time here, so waiting for the server is fine —
    // and it means a refused save reverts the cell instead of resolving on a
    // 50ms timer and looking saved.
    await Promise.all(saves);
    return { ..._newRow };
  };

  const getColumns = () => {
    const ret: GridColumns<any> = [
      {
        field: "taskType",
        headerName: "Task Type",
        width: 150,
      },
    ];

    projectTypes?.forEach((pt) => {
      ret.push({
        field: pt.name,
        headerName: pt.archived ? `${pt.name} (Archived)` : pt.name,
        width: 150,
        editable: true,
        type: "number",
        align: "right",
        headerAlign: "center",
        valueGetter: (_value: any, row: any) => {
          if (row?.[pt.name]?.rate === undefined) return undefined;
          return row[pt.name].rate / 10000;
        },
        valueSetter: (value: any, row: any) => {
          const ret = JSON.parse(JSON.stringify(row));
          ret[pt.name].rate = toScaledInteger(value, 10000);

          return ret;
        },
        renderCell: (p) => {
          if (!p.value) {
            console.log(
              taskTypes?.find((t) => t.name === p.row.taskType),
              pt
            );
          }
          return p.value !== undefined ? (
            <span>{p.value}</span>
          ) : (
            <Chip
              variant="outlined"
              onClick={() => {
                createScale({
                  projectTypeID: pt.id,
                  taskTypeID: taskTypes?.find((t) => t.name === p.row.taskType)
                    ?.id,
                  rate: 0,
                });
              }}
              size="sm"
            >
              + Create
            </Chip>
          );
        },
        valueFormatter: (valueOrParams: any) => {
          const value = valueOrParams?.value ?? valueOrParams;
          return value
            ? `${currencyFormatter.format(value)}`.substring(1)
            : "No Set";
        },
      });
    });

    return ret;
  };

  const getRows = () => {
    const ret: any[] = [];
    if (jobCategoryID === 2) {
      const row = {
        id: 0,
        taskType: "Minimum Pay",
      };
      projectTypes?.forEach((pt) => {
        row[pt.name] = { id: 0, rate: pt.minimumCopyist, projectTypeID: pt.id };
      });

      ret.push(row);
    }
    taskTypes?.forEach((tt) => {
      const row = {
        id: tt.id,
        taskType: tt.name,
        archived: tt.archived,
      };
      projectTypes?.forEach((pt) => {
        const scale = scales?.find(
          (s) => s.projectTypeID === pt.id && s.taskTypeID === tt.id
        );
        row[pt.name] = scale;
      });
      ret.push(row);
    });

    return ret;
  };

  return (
    <Paper
      sx={{
        flex: 1,
        height: "calc(100vh - 130px)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <DataGridPro
        apiRef={apiRef}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={reportSaveError}
        {...defaultDataGridProps}
        onCellKeyDown={handleCellKeyDown}
        rows={getRows()}
        columns={getColumns()}
        getRowClassName={(p) => (p.row.archived ? "archived" : "")}
        showToolbar
        slots={{
          toolbar: RatesToolbar,
        }}
        slotProps={{
          cell: { tabIndex: 1 },
        }}
        initialState={{
          pinnedColumns: {
            left: ["taskType"],
          },
        }}
      />
      {saveErrorSnackbar}
    </Paper>
  );
}

function RatesToolbar() {
  const dispatch = useDispatch();
  const showArchived = useSelector(showArchivedRates);
  const [archiveOpen, setArchiveOpen] = React.useState(false);

  return (
    <Toolbar>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}
      >
        <Button
          variant="soft"
          size="sm"
          startDecorator={<ArchiveIcon fontSize="small" />}
          onClick={() => setArchiveOpen(true)}
        >
          Archive &amp; Restore
        </Button>
        <QuickFilter
          expanded
          style={{ flex: 1, display: "flex", alignItems: "center" }}
        >
          <QuickFilterControl size="small" fullWidth placeholder="Search..." />
          <QuickFilterClear />
        </QuickFilter>
        <Checkbox
          size="sm"
          variant="outlined"
          label="Show Archived"
          onChange={(e) => dispatch(setShowArchivedRates(e.target.checked))}
          checked={showArchived}
        />
      </Box>
      <ArchiveColumnsDialog
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
      />
    </Toolbar>
  );
}
